import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Sparkle, PaperPlaneRight } from "@phosphor-icons/react";

const ROUTES = [
  { q: "Where can we submit today?", to: "/console/submissions", keys: ["submit", "submission", "where can we"] },
  { q: "What evidence is missing?", to: "/console/gaps", keys: ["missing", "gap", "blocking", "lack"] },
  { q: "Which countries can reuse our evidence?", to: "/console/reusability", keys: ["reuse", "reusab", "travel", "adapt"] },
  { q: "Which study should we run next?", to: "/console/whatif", keys: ["study", "run next", "invest", "scenario", "what if", "simulate"] },
  { q: "Assess our Global Value Dossier", to: "/console/gvd", keys: ["gvd", "dossier", "value dossier"] },
  { q: "Build our launch roadmap", to: "/console/roadmap", keys: ["roadmap", "launch", "expansion"] },
  { q: "Monitor regulatory changes", to: "/console/monitor", keys: ["monitor", "regulat", "change", "news", "update", "hta change"] },
  { q: "Who was reimbursed before us?", to: "/console/competitive", keys: ["competitor", "precedent", "reimbursed before", "landscape"] },
];
const WAVE_VERDICT = {
  1: ["Submit Today", "#047857", "All mandatory requirements are met — initiate the submission."],
  2: ["Minor Localization", "#B45309", "The evidence exists but needs local adaptation — weeks of work, not years."],
  3: ["Evidence Required", "#B91C1C", "Readiness is below the submission threshold — new evidence or substantial adaptation is required first."],
};

export const AskMAI = ({ countries }) => {
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [answer, setAnswer] = useState(null);

  const answerCountry = (c) => setAnswer(c);

  const ask = (e) => {
    e?.preventDefault();
    const q = text.toLowerCase();
    if (!q.trim()) return;
    const country = countries.find((c) => q.includes(c.name.toLowerCase()));
    if (country) return answerCountry(country);
    const route = ROUTES.find((r) => r.keys.some((k) => q.includes(k)));
    navigate(route ? route.to : "/console/decisions");
  };

  const [verdict, vColor, vText] = answer ? WAVE_VERDICT[answer.wave] : [];
  return (
    <div data-testid="ask-mai-panel" className="bg-slate-900 text-white p-6 mb-8">
      <div className="flex items-center gap-2">
        <Sparkle size={16} weight="fill" className="text-emerald-400" />
        <p className="overline-label text-emerald-400">Ask MAI</p>
      </div>
      <form onSubmit={ask} className="flex gap-2 mt-3">
        <input data-testid="ask-mai-input" value={text} onChange={(e) => setText(e.target.value)}
          placeholder="What would you like to know? e.g. “Are we ready for Japan?”"
          className="flex-1 bg-white/5 border border-white/15 text-sm px-4 py-3 placeholder:text-slate-500 focus:outline-none focus:border-emerald-500" />
        <button data-testid="ask-mai-submit" className="bg-emerald-600 hover:bg-emerald-500 px-4 flex items-center">
          <PaperPlaneRight size={16} weight="bold" />
        </button>
      </form>
      <div className="flex flex-wrap items-center gap-2 mt-3">
        <select data-testid="ask-country-select" value="" onChange={(e) => {
          const c = countries.find((x) => x.id === e.target.value);
          if (c) answerCountry(c);
        }} className="text-[11px] bg-white/5 border border-white/15 text-slate-300 px-2.5 py-1.5 focus:outline-none cursor-pointer">
          <option value="" disabled>Are we ready for… ▾</option>
          {countries.map((c) => <option key={c.id} value={c.id} className="text-slate-900">{c.name}</option>)}
        </select>
        {ROUTES.map((r, i) => (
          <button key={r.q} data-testid={`ask-chip-${i}`} onClick={() => navigate(r.to)}
            className="text-[11px] text-slate-300 border border-white/15 px-2.5 py-1.5 hover:border-emerald-500 hover:text-white transition-colors">
            {r.q}
          </button>
        ))}
      </div>
      {answer && (
        <div data-testid="ask-answer-card" className="mt-4 border-t border-white/10 pt-4 flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1">
            <p className="text-sm font-semibold">
              {answer.flag} {answer.name}: <span style={{ color: vColor === "#047857" ? "#34D399" : vColor === "#B45309" ? "#FBBF24" : "#F87171" }}>{verdict}</span>
              <span className="font-mono-data text-white/60 ml-2">readiness {answer.readiness_score}/100</span>
            </p>
            <p className="text-xs text-white/60 leading-relaxed mt-1">
              {vText} {answer.requirements_satisfied} requirements met · {answer.requirements_partial} need adaptation · {answer.requirements_missing} missing · ~{answer.months_to_decision} months to decision{answer.top_blocker ? ` · top blocker: ${answer.top_blocker}` : ""}.
            </p>
          </div>
          <button data-testid="ask-answer-gaps-cta" onClick={() => navigate("/console/gaps")}
            className="shrink-0 text-xs font-semibold border border-white/20 px-4 py-2.5 hover:bg-white/5 flex items-center gap-1.5">
            See the exact gaps <ArrowRight size={12} weight="bold" />
          </button>
        </div>
      )}
    </div>
  );
};
